// FILE: FilePreviewSection.tsx
// Purpose: Settings → Advanced → File preview: glob patterns an absolute
//          path must match for `files.preview` (chat's detected file-path
//          links) to read it, for the whole installation.
// Layer: Web settings presentation
// Exports: FilePreviewSection

import { useCallback, useEffect, useState } from "react";

import { PlusIcon, XIcon } from "~/lib/icons";
import { SystemSettingsClient } from "~/lib/systemSettingsClient";

import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * No optimistic update, matching `GenerationDefaultsSection`: each add/remove
 * reflects the server's answer rather than an assumed one, so a failed save
 * leaves the previous list visible with the reason next to it.
 */
export function FilePreviewSection() {
  const [allowedGlobs, setAllowedGlobs] = useState<readonly string[] | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void SystemSettingsClient.readFilePreview()
      .then((value) => {
        if (!cancelled) setAllowedGlobs(value.allowedGlobs);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof Error ? cause.message : "Could not load the file preview setting.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(async (next: readonly string[]) => {
    setIsSaving(true);
    setError(null);
    try {
      const stored = await SystemSettingsClient.writeFilePreview({ allowedGlobs: next });
      setAllowedGlobs(stored.allowedGlobs);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not save the change.");
    } finally {
      setIsSaving(false);
    }
  }, []);

  const addPattern = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed.length === 0 || allowedGlobs === null) return;
    setDraft("");
    if (allowedGlobs.includes(trimmed)) return;
    void persist([...allowedGlobs, trimmed]);
  }, [draft, allowedGlobs, persist]);

  const removePattern = useCallback(
    (pattern: string) => {
      if (allowedGlobs === null) return;
      void persist(allowedGlobs.filter((entry) => entry !== pattern));
    },
    [allowedGlobs, persist],
  );

  if (error !== null && allowedGlobs === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {error}
      </p>
    );
  }

  if (allowedGlobs === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        Loading file preview settings…
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-border/70 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">Allowed preview locations</p>
        <p className="text-xs text-muted-foreground">
          When chat mentions an absolute file path — a plan or decision an agent wrote outside a
          project — users can open a read-only preview if it matches one of these glob patterns.
          <code className="mx-1">*</code>
          matches within one path segment,<code className="mx-1">**</code>
          crosses directories. A project can add its own patterns via{" "}
          <code>.taskin/config.json</code>, on top of this list.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            addPattern();
          }}
          placeholder="/tmp/claude/plans/*"
          spellCheck={false}
          disabled={isSaving}
          aria-label="New glob pattern"
        />
        <Button className="shrink-0" variant="outline" onClick={addPattern} disabled={isSaving}>
          <PlusIcon className="size-3.5" />
          Add
        </Button>
      </div>

      {allowedGlobs.length > 0 ? (
        <div>
          {allowedGlobs.map((pattern) => (
            <div
              key={pattern}
              className="group flex items-center justify-between gap-3 border-t border-border/60 px-1 py-2 first:border-t-0"
            >
              <code className="min-w-0 truncate text-sm text-foreground">{pattern}</code>
              <button
                type="button"
                className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:opacity-100"
                aria-label={`Remove ${pattern}`}
                onClick={() => removePattern(pattern)}
                disabled={isSaving}
              >
                <XIcon className="size-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No locations allowed yet — detected file paths in chat stay inert.
        </p>
      )}

      {error !== null ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
